import { Spinner } from "@heroui/react";
import React, { useState } from "react";
import { OpportunityViewModal } from "../opportunities/view/opportunityViewModal";
import ViewModal from "../readyRoster/view/viewModal";
import CreateEditReadyRoster from "../readyRoster/createEdit/createEditReadyRoster";
import MarketplaceCard from "./marketCard";
import MarketplaceCardsFilters from "./marketplaceFilters";
import { useMarketplaceCards } from "./useMarketplaceCards";
import { MarketplaceCardResponseDto } from "../../types/types";
import OpportunityFormContainer from "../opportunities/create/opportunityFormContainer";
import DealWizardModal from "../opportunities/create/dealWizardModal";

const MarketplaceCards: React.FC = () => {
  const {
    t,
    profileType,
    managerId,
    loginResponse,
    activeTab,
    setActiveTab,
    currentMonth,
    setCurrentMonth,
    currencies,
    confirmContactPopup,
    setConfirmContactPopup,
    selectedRosterStatus,
    setSelectedRosterStatus,
    selectedTitle,
    setSelectedTitle,
    searchName,
    setSearchName,
    filterCardType,
    setFilterCardType,
    filterLocation,
    setFilterLocation,
    filterSport,
    setFilterSport,
    filterWeightClass,
    setFilterWeightClass,
    filterPurse,
    setFilterPurse,
    filterCurrency,
    setFilterCurrency,
    filterOpportunityType,
    setFilterOpportunityType,
    cards,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    showReportPopup,
    setShowReportPopup,
    currentPage,
    setCurrentPage,
    totalPages,
    isCreateRosterModalOpen,
    setIsCreateRosterModalOpen,
    isOpportunityModalOpen,
    setIsOpportunityModalOpen,
    isViewModalOpen,
    isEditModalOpen,
    selectedCard,
    selectedOpportunity,
    setSelectedOpportunity,
    viewCardId,
    isOpportunityViewOpen,
    setIsOpportunityViewOpen,
    isPublishing,
    confirmPublishPopup,
    setConfirmPublishPopup,
    locations,
    sports,
    weightClasses,
    purses,
    opportunityTypes,
    rosterStatus,
    fetchMarketplaceCards,
    handleResetFilters,
    handleViewModalClose,
    handleEditModalClose,
    handlePublish,
    handleEdit,
    handleDelete,
    handleView,
    handleOpportunityView,
    handleCreateContact,
    loadPursesIfNeeded,
    loadCurrenciesIfNeeded,
    loadWeightClassesIfNeeded,
    loadSportsIfNeeded,
    loadOpportunityTypeIfNeeded,
    connectStatusLoading,
    connectStatus,
    handleCreateOpportunity,
    templates,
    templatesLoading,
  } = useMarketplaceCards();

  const observerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    const target = observerRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { root: null, rootMargin: "300px", threshold: 0 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, loadMore]);

  const [isTemplateSelectorOpen, setIsTemplateSelectorOpen] = useState(false);

  const openTemplateSelector = async () => {
    await handleCreateOpportunity();
    setIsTemplateSelectorOpen(true);
  };

  const closeTemplateSelector = () => {
    setIsTemplateSelectorOpen(false);
  };

  // Mobile/tablet unchanged; desktop follows Figma (24px gap, ~278px cards => 6 columns at 1920px).
  const gridClass =
    "grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fill,minmax(256px,1fr))]";

  const isRosterVisible = (item: MarketplaceCardResponseDto) =>
    !!item.readyRosterCard &&
    (activeTab === "all" || profileType === "FIGHTER");

  const isOpportunityVisible = (item: MarketplaceCardResponseDto) =>
    !!item.opportunityCardResponse &&
    (activeTab === "all" ||
      profileType === "MANAGER" ||
      profileType === "ORGANIZATION");

  return (
    <div className="w-full h-auto min-h-screen lg:h-[90vh] flex flex-col bg-gray-50 lg:bg-neutral-100">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 lg:border-b-0 px-4 lg:px-6 py-3 lg:py-6 flex-shrink-0">
        <div className="max-w-full mx-auto">
          <MarketplaceCardsFilters
            activeTab={activeTab}
            onTabChange={(key) => setActiveTab(key as "all" | "myCards")}
            profileType={profileType}
            searchName={searchName}
            setSearchName={setSearchName}
            filterCardType={filterCardType}
            onCreateOpportunity={openTemplateSelector}
            setFilterCardType={setFilterCardType}
            filterLocation={filterLocation}
            setFilterLocation={setFilterLocation}
            filterSport={filterSport}
            setFilterSport={setFilterSport}
            filterWeightClass={filterWeightClass}
            setFilterWeightClass={setFilterWeightClass}
            filterPurse={filterPurse}
            setFilterPurse={setFilterPurse}
            filterCurrency={filterCurrency}
            setFilterCurrency={setFilterCurrency}
            filterOpportunityType={filterOpportunityType}
            setFilterOpportunityType={setFilterOpportunityType}
            selectedRosterStatus={selectedRosterStatus}
            setSelectedRosterStatus={setSelectedRosterStatus}
            selectedTitle={selectedTitle}
            setSelectedTitle={setSelectedTitle}
            locations={locations}
            sports={sports}
            weightClasses={weightClasses}
            purses={purses}
            opportunityTypes={opportunityTypes}
            rosterStatus={rosterStatus}
            handleResetFilters={handleResetFilters}
            setIsCreateRosterModalOpen={setIsCreateRosterModalOpen}
            setIsOpportunityModalOpen={setIsOpportunityModalOpen}
            connectStatus={connectStatus}
            connectStatusLoading={connectStatusLoading}
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="max-w-full mx-auto p-4 lg:p-6 pb-20">
          {loading ? (
            <div className={gridClass}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="w-full h-96 rounded-3xl bg-white flex flex-col shadow-sm border border-slate-100 animate-pulse"
                >
                  <div className="p-1 flex flex-col gap-2 flex-shrink-0">
                    <div className="h-[195px] rounded-[20px] bg-slate-200/80" />
                    <div className="h-5 w-full rounded-full bg-slate-200/60" />
                  </div>

                  <div className="flex-1 min-h-0 px-4 pt-1 pb-4 flex flex-col justify-between">
                    <div className="h-12 flex justify-between items-start gap-2">
                      <div className="flex-1 space-y-1.5">
                        <div className="h-5 w-3/4 rounded-lg bg-slate-200" />
                        <div className="h-3 w-1/2 rounded bg-slate-100" />
                      </div>
                      <div className="h-4 w-14 rounded bg-slate-200" />
                    </div>
                    {[24, 28, 32].map((width) => (
                      <div key={width} className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-slate-200 shrink-0" />
                        <div
                          className="h-3.5 rounded bg-slate-200"
                          style={{ width: `${width * 4}px` }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : cards.length > 0 ? (
            <div className={gridClass}>
              {cards.map((item: MarketplaceCardResponseDto) => {
                if (isRosterVisible(item)) {
                  return (
                    <MarketplaceCard
                      key={`roster-${item.id}`}
                      item={item}
                      role={profileType}
                      activeTab={activeTab}
                      onView={() => handleView(item)}
                      onEdit={handleEdit}
                      onDelete={() => handleDelete(item)}
                    />
                  );
                }
                if (isOpportunityVisible(item)) {
                  return (
                    <MarketplaceCard
                      key={`opportunity-${item.id}`}
                      item={item}
                      role={profileType}
                      activeTab={activeTab}
                      onView={() => handleOpportunityView(item.id)}
                      onEdit={handleEdit}
                      onDelete={() => handleDelete(item)}
                    />
                  );
                }
                return null;
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-16 h-16 rounded-full bg-default-100 flex items-center justify-center">
                <svg
                  className="w-7 h-7 text-default-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H6.911a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661Z"
                  />
                </svg>
              </div>
              <p className="text-default-400 text-sm">{t("no_cards_found")}</p>
            </div>
          )}

          {cards.length > 0 && hasMore && (
            <div ref={observerRef} className="w-full flex items-center justify-center py-8">
              {loadingMore && <Spinner size="md" color="primary" />}
            </div>
          )}
        </div>
      </div>

      <CreateEditReadyRoster
        isOpen={isCreateRosterModalOpen}
        onClose={() => setIsCreateRosterModalOpen(false)}
        fetchData={fetchMarketplaceCards}
      />

      <CreateEditReadyRoster
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        initialData={selectedCard || undefined}
        fetchData={fetchMarketplaceCards}
      />

      {selectedCard && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={handleViewModalClose}
          viewCard={selectedCard}
          marketplaceId={selectedCard.id}
          role={profileType}
          activeTab={activeTab}
          loggedInUserProfileType={profileType}
          loggedInUserName={loginResponse?.username || ""}
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          selectedDates={
            new Set<string>(selectedCard?.readyRosterCard?.availableDays || [])
          }
          fetchMarketplaceCards={fetchMarketplaceCards}
          isPublishing={isPublishing}
          confirmPublishPopup={confirmPublishPopup}
          setConfirmPublishPopup={setConfirmPublishPopup}
          confirmContactPopup={confirmContactPopup}
          setConfirmContactPopup={setConfirmContactPopup}
          showReportPopup={showReportPopup}
          setShowReportPopup={setShowReportPopup}
          handlePublish={handlePublish}
          handleCreateContact={handleCreateContact}
        />
      )}

      {viewCardId && (
        <OpportunityViewModal
          isOpen={isOpportunityViewOpen}
          onClose={() => setIsOpportunityViewOpen(false)}
          cardId={viewCardId}
          activeTab={activeTab}
          onRefresh={fetchMarketplaceCards}
        />
      )}
      <DealWizardModal
        isOpen={isTemplateSelectorOpen}
        onClose={closeTemplateSelector}
        templates={templates}
        templatesLoading={templatesLoading}
        onCreated={fetchMarketplaceCards}
      />

      <OpportunityFormContainer
        isModalOpen={isOpportunityModalOpen}
        setModalOpen={setIsOpportunityModalOpen}
        selectedCard={selectedOpportunity || null}
        setSelectedCard={setSelectedOpportunity}
        templates={templates}
        managerId={managerId}
        fetchData={fetchMarketplaceCards}
      />
    </div>
  );
};

export default MarketplaceCards;